import { normalizeNfcUid } from 'lib/models/nfcCards';

/**
 * Why a read could not go on: the browser refused NFC (`denied`), the phone has it turned
 * off or has none (`unsupported`), it could not start for any other reason (`startFailed`),
 * a card was brought near but not read (`readFailed`) or what it read is not a card UID
 * (`invalidUid`).
 */
export type NfcReadFailure = 'denied' | 'unsupported' | 'startFailed' | 'readFailed' | 'invalidUid';

export type NfcScanHandlers = {
	/** Receives the UID of every card read, normalized as the API stores it. */
	onUid: (uid: string) => void;
	/** Receives a card that was detected but could not be read, or read as no valid UID. */
	onFailure: (failure: NfcReadFailure) => void;
};

/**
 * Whether this browser can read NFC cards: only Chrome for Android, and only from a secure
 * context (HTTPS, or localhost). Anywhere else the panel cannot register cards, and a card is
 * found by typing its UID.
 */
export function isWebNfcAvailable(): boolean {
	return typeof window !== 'undefined' && window.isSecureContext && 'NDEFReader' in window;
}

/**
 * Starts reading cards until `signal` is aborted. It has to be called from a user gesture:
 * Chrome refuses to start a read otherwise, and asks for the permission the first time.
 *
 * Only the serial number is read, which is the card UID the bar reads with the native reader:
 * MIFARE Classic cards carry no NDEF message the browser can read, and none is needed.
 *
 * @resolves Once the device is listening for cards
 *
 * @throws What `NDEFReader.scan` threw (see `getNfcScanFailure`)
 */
export async function startNfcScan(signal: AbortSignal, { onUid, onFailure }: NfcScanHandlers): Promise<void> {
	const reader = new NDEFReader();

	reader.addEventListener('reading', (event) => {
		if (signal.aborted) return;

		const uid = normalizeNfcUid(event.serialNumber || '');

		if (uid) {
			onUid(uid);
		} else {
			onFailure('invalidUid');
		}
	});

	reader.addEventListener('readingerror', () => {
		if (signal.aborted) return;

		onFailure('readFailed');
	});

	await reader.scan({ signal });
}

/**
 * Why a read could not start, from what `startNfcScan` threw, or null when it was stopped on
 * purpose (the screen or the modal was closed while it was starting).
 *
 * @param error What `startNfcScan` threw
 */
export function getNfcScanFailure(error: unknown): NfcReadFailure | null {
	const name = error instanceof DOMException || error instanceof Error ? error.name : '';

	switch (name) {
		case 'AbortError':
			return null;
		case 'NotAllowedError':
			return 'denied';
		case 'NotSupportedError':
		case 'NotReadableError':
			return 'unsupported';
		default:
			return 'startFailed';
	}
}
